<script setup>
import { MediaImage } from '@drama/media-delivery/vue'
import { useI18n } from 'vue-i18n'
import VgIcon from './trpg/icons/VgIcon.vue'
import { liveRoomHref } from '../lib/h3live.js'
import { useH3liveRooms } from '../stores/h3live-rooms.js'

const { t } = useI18n()
// 数据来自宿主服务端 /api/sk/h3live/rooms；没接 h3live 或上游不可用时列表为空，区块整个不渲染。
const { channels } = useH3liveRooms()

function coverUrl(channel) {
  return channel.poster_url || channel.cover_url || ''
}
</script>

<template>
  <section v-if="channels.length > 0" class="live-rooms" aria-labelledby="landing-live-title">
    <header class="live-rooms-header">
      <h2 id="landing-live-title">{{ t('visualGame.landingLive.title') }}</h2>
      <a class="live-rooms-more" href="/live" :aria-label="t('visualGame.landingIntro.viewMore')">
        <VgIcon name="back" :size="18" />
      </a>
    </header>
    <div class="live-rooms-grid">
      <a v-for="channel in channels" :key="channel.id" class="live-room" :href="liveRoomHref(channel.key)" :aria-label="channel.name">
        <span class="live-room-cover">
          <MediaImage v-if="coverUrl(channel)" sizes="(max-width: 680px) 40vw, (max-width: 980px) 33vw, 20vw" :max_width="960" :src="coverUrl(channel)" :alt="channel.name" loading="lazy" decoding="async" />
          <span v-else class="live-room-fallback" aria-hidden="true">{{ channel.name.slice(0, 1) }}</span>
        </span>
        <span class="live-room-copy">
          <strong>{{ channel.name }}</strong>
          <span class="live-room-meta">
            <span class="live-room-on-air">{{ t('visualGame.landingLive.badge') }}</span>
            <span v-if="channel.watching > 0"><VgIcon name="eye" :size="12" />{{ t('visualGame.landingLive.watching', { count: channel.watching }) }}</span>
          </span>
        </span>
      </a>
    </div>
  </section>
</template>

<style scoped>
/* 和首页 Games 区块的海报列表(LandingPage.vue 里 .story-discovery-grid 下的 .story-poster)一模一样：
 * 同一套区块内边距、标题、五列栅格、9:16 圆角封面、图下两行文字。那些规则是 LandingPage 的 scoped
 * 样式，子组件够不着，所以按它渲染出来的实际值照抄；根元素故意不叫 landing-section——父级的同名
 * scoped 规则会穿到子组件根节点上，把这里的内边距盖掉(上一版就是这么错的)。 */
.live-rooms { display: grid; align-content: start; gap: clamp(var(--drama-space-lg), 2.4vw, 34px); padding: 32px clamp(24px, 3vw, 48px) 40px; border-top: 1px solid color-mix(in srgb, var(--drama-color-border) 72%, transparent); background: var(--drama-color-bg); }
.live-rooms-header { display: flex; align-items: center; justify-content: space-between; gap: var(--drama-space-xl); }
.live-rooms-header h2 { margin: 0; font-family: var(--drama-font-family-body); font-size: clamp(26px, 2.5vw, 32px); font-weight: var(--drama-font-weight-bold); letter-spacing: -.025em; line-height: 1.12; }
.live-rooms-more { display: grid; width: 32px; height: 32px; flex: none; place-items: center; border-radius: var(--drama-radius-full); color: var(--drama-color-text-muted); text-decoration: none; transition: background 160ms ease, color 160ms ease, transform 160ms ease; }
.live-rooms-more :deep(.vg-icon) { transform: rotate(180deg); }
.live-rooms-more:hover, .live-rooms-more:focus-visible { background: var(--drama-color-surface-alt); color: var(--drama-color-text); transform: translateX(2px); }
.live-rooms-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: clamp(12px, 1.4vw, 22px); }
.live-room { display: grid; min-width: 0; align-content: start; color: var(--drama-color-text); text-decoration: none; }
.live-room-cover { position: relative; display: block; overflow: hidden; aspect-ratio: 9 / 16; border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); }
.live-room-cover img { display: block; width: 100%; height: 100%; object-fit: cover; transition: transform .5s ease; }
.live-room:hover .live-room-cover img, .live-room:focus-visible .live-room-cover img { transform: scale(1.045); }
.live-room:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 2px; border-radius: var(--drama-radius-md); }
.live-room-fallback { display: grid; height: 100%; place-items: center; color: color-mix(in srgb, var(--drama-color-primary) 72%, transparent); font-family: var(--vgs-font-serif); font-size: clamp(64px, 8vw, 120px); }
.live-room-copy { display: grid; gap: 2px; padding-top: 8px; }
.live-room-copy strong { display: -webkit-box; overflow: hidden; font-family: var(--drama-font-family-body); font-size: 14px; font-weight: var(--drama-font-weight-semibold); letter-spacing: 0; line-height: 1.35; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.live-room-meta { display: flex; min-width: 0; align-items: center; gap: 8px; color: var(--drama-color-text-muted); font-size: 11px; line-height: 18px; }
.live-room-meta > span { display: inline-flex; min-width: 0; align-items: center; gap: 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.live-room-meta > span + span::before { margin-right: 5px; content: '·'; }
.live-room-on-air { color: var(--drama-color-danger); font-weight: var(--drama-font-weight-semibold); letter-spacing: .04em; }
.live-room-on-air::before { width: 6px; height: 6px; border-radius: var(--drama-radius-full); background: currentColor; content: ''; }
@media (max-width: 980px) {
  .live-rooms-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 680px) {
  .live-rooms { padding: 24px var(--drama-space-lg) 32px; gap: 12px; }
  .live-rooms-header { position: relative; min-height: 40px; }
  .live-rooms-header h2 { font-size: 24px; }
  .live-rooms-more { position: absolute; inset: 0; display: flex; width: auto; height: auto; align-items: center; justify-content: flex-end; border-radius: 0; }
  .live-rooms-more:hover, .live-rooms-more:focus-visible { background: transparent; transform: none; }
  .live-rooms-grid { grid-auto-columns: 40%; grid-auto-flow: column; grid-template-columns: none; gap: 8px; overflow-x: auto; overscroll-behavior-inline: contain; padding-bottom: 4px; scroll-snap-type: inline proximity; scrollbar-width: none; }
  .live-rooms-grid::-webkit-scrollbar { display: none; }
  .live-room { scroll-snap-align: start; }
}
</style>
